import { useCallback, useMemo } from 'react';
import type { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from 'lucide-react';
import { de } from 'react-day-picker/locale';
import { Calendar as CalendarComponent } from 'components/calendar';
import {
  DropdownMenu,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/dropdown/dropdown';
import { formatReadableDate } from 'utils';
import { TimeRange } from 'screens/home/utils';
import {
  CalendarIcon,
  CalendarWrapper,
  ChevronDownIcon,
  CustomRangeContainer,
  CustomRangeLabel,
  DateRangeButton,
  DateRangeButtonContent,
  DropdownItemCheckWrapper,
  DropdownItemText,
  TimeRangeDropdownContent,
  TimeRangeDropdownGroup,
  TimeRangeDropdownItem,
} from './DateFilter.styles';
import { getDateRange } from 'widgets/home/utils';

interface DateFilterProps {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
}

const PRESET_PRIORITY = [
  TimeRange.Today,
  TimeRange.Yesterday,
  TimeRange.ThisWeek,
  TimeRange.LastWeek,
];

const rangesMatch = (a: DateRange | undefined, b: DateRange): boolean =>
  !!a && a.from?.getTime() === b.from?.getTime() && a.to?.getTime() === b.to?.getTime();

export const DateFilter = ({ dateRange, onDateRangeChange }: DateFilterProps) => {
  const { t } = useTranslation();

  const activePreset = useMemo(
    () => PRESET_PRIORITY.find((preset) => rangesMatch(dateRange, getDateRange(preset))),
    [dateRange]
  );

  const isPresetSelected = useCallback(
    (range: TimeRange) => activePreset === range,
    [activePreset]
  );

  const isCalendarDayDisabled = useCallback(
    (date: Date) => {
      if (date <= new Date()) return false;
      return !(dateRange?.from && dateRange?.to && date >= dateRange.from && date <= dateRange.to);
    },
    [dateRange]
  );

  const timeRangeLabel = useMemo(() => {
    switch (activePreset) {
      case TimeRange.Today:
        return t('home.timeRanges.today', {
          date: formatReadableDate(new Date().toISOString()),
        });
      case TimeRange.Yesterday:
        return t('home.timeRanges.yesterday');
      case TimeRange.ThisWeek:
        return t('home.timeRanges.thisWeek');
      case TimeRange.LastWeek:
        return t('home.timeRanges.lastWeek');
      default:
        if (dateRange?.from) {
          return `${formatReadableDate(dateRange.from)}${
            dateRange.to ? ` - ${formatReadableDate(dateRange.to)}` : ''
          }`;
        }
        return t('home.timeRanges.today', {
          date: formatReadableDate(new Date().toISOString()),
        });
    }
  }, [activePreset, dateRange, t]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <DateRangeButton>
          <DateRangeButtonContent>
            <CalendarIcon />
            <span>{timeRangeLabel}</span>
          </DateRangeButtonContent>
          <ChevronDownIcon />
        </DateRangeButton>
      </DropdownMenuTrigger>
      <TimeRangeDropdownContent>
        <TimeRangeDropdownGroup>
          <TimeRangeDropdownItem
            $isSelected={isPresetSelected(TimeRange.Today)}
            onClick={() => onDateRangeChange(getDateRange(TimeRange.Today))}
          >
            <DropdownItemText>
              {t('home.timeRanges.today', {
                date: formatReadableDate(new Date().toISOString()),
              })}
            </DropdownItemText>
            <DropdownItemCheckWrapper>
              {isPresetSelected(TimeRange.Today) && <CheckIcon className="w-4 h-4" />}
            </DropdownItemCheckWrapper>
          </TimeRangeDropdownItem>
          <TimeRangeDropdownItem
            $isSelected={isPresetSelected(TimeRange.Yesterday)}
            onClick={() => onDateRangeChange(getDateRange(TimeRange.Yesterday))}
          >
            <DropdownItemText>{t('home.timeRanges.yesterday')}</DropdownItemText>
            <DropdownItemCheckWrapper>
              {isPresetSelected(TimeRange.Yesterday) && <CheckIcon className="w-4 h-4" />}
            </DropdownItemCheckWrapper>
          </TimeRangeDropdownItem>
          <TimeRangeDropdownItem
            $isSelected={isPresetSelected(TimeRange.ThisWeek)}
            onClick={() => onDateRangeChange(getDateRange(TimeRange.ThisWeek))}
          >
            <DropdownItemText>{t('home.timeRanges.thisWeek')}</DropdownItemText>
            <DropdownItemCheckWrapper>
              {isPresetSelected(TimeRange.ThisWeek) && <CheckIcon className="w-4 h-4" />}
            </DropdownItemCheckWrapper>
          </TimeRangeDropdownItem>
          <TimeRangeDropdownItem
            $isSelected={isPresetSelected(TimeRange.LastWeek)}
            onClick={() => onDateRangeChange(getDateRange(TimeRange.LastWeek))}
          >
            <DropdownItemText>{t('home.timeRanges.lastWeek')}</DropdownItemText>
            <DropdownItemCheckWrapper>
              {isPresetSelected(TimeRange.LastWeek) && <CheckIcon className="w-4 h-4" />}
            </DropdownItemCheckWrapper>
          </TimeRangeDropdownItem>
        </TimeRangeDropdownGroup>
        <DropdownMenuSeparator />
        <CustomRangeContainer>
          <CustomRangeLabel>{t('home.timeRanges.custom')}</CustomRangeLabel>
          <CalendarWrapper>
            <CalendarComponent
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={onDateRangeChange}
              numberOfMonths={1}
              locale={de}
              disabled={isCalendarDayDisabled}
            />
          </CalendarWrapper>
        </CustomRangeContainer>
      </TimeRangeDropdownContent>
    </DropdownMenu>
  );
};
